import { useEffect, useMemo, useState } from 'react';
import ReceivableSelectionItem from './ReceivableSelectionItem';
import SearchSelectionCardHeader from './SearchSelectionCardHeader';
import SearchSelectionFilters from './SearchSelectionFilters';

import { SearchSelectionCardProps } from './types';
import { ChartNoAxesColumn } from 'lucide-react';

const SearchSelectionCard = ({
  title,
  description,
  label,
  name,
  iconKey = 'search',
  options,
  selectedValue,
  onSelect,
  onSearch,
  listTitle = 'Cuentas disponibles',
  emptyMessage = 'No hay registros disponibles',
  disabled = false,
  required = false,
}: SearchSelectionCardProps) => {
  const [search, setSearch] = useState('');
  const [searchDate, setSearchDate] = useState('');

  /**
   * 🔥 limpiar filtros
   */
  const handleClearFilters = () => {
    setSearch('');
    setSearchDate('');

    /**
     * si usa búsqueda backend
     * volvemos a cargar sin filtros
     */
    if (onSearch) {
      onSearch('');
    }
  };

  /**
   * 🔥 filtro local
   * si existe onSearch → backend search
   * si no → filtro local
   */
  const filteredOptions = useMemo(() => {
    /**
     * backend search
     */
    if (onSearch) return options;

    /**
     * sin filtros
     */
    if (!search.trim() && !searchDate) {
      return options;
    }

    const query = search.toLowerCase();

    return options.filter((item) => {
      const matchText =
        !search.trim() ||
        item.number.toLowerCase().includes(query) ||
        item.issueDate.toLowerCase().includes(query) ||
        item.dueDate.toLowerCase().includes(query);

      const matchDate = !searchDate || item.issueDate === searchDate;

      return matchText && matchDate;
    });
  }, [search, searchDate, options, onSearch]);

  /**
   * 🔥 búsqueda backend con debounce
   */
  useEffect(() => {
    if (!onSearch) return;

    const timeout = setTimeout(() => {
      const finalSearch = searchDate?.trim() ? searchDate : search.trim();

      onSearch(finalSearch);
    }, 400);

    return () => clearTimeout(timeout);
  }, [search, searchDate]);

  return (
    <div className="rounded-md border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      {/* Header */}
      <SearchSelectionCardHeader
        title={title}
        description={description}
        iconKey={iconKey}
      />

      {/* Filters */}
      <SearchSelectionFilters
        label={label}
        name={name}
        search={search}
        searchDate={searchDate}
        onSearchChange={setSearch}
        onDateChange={setSearchDate}
        onClear={handleClearFilters}
        disabled={disabled}
        required={required}
      />

      {/* List */}
      <div className="px-6 py-6">
        {/* Top info */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-black dark:text-white">
              {listTitle}
            </h4>

            <p className="mt-1 text-xs text-body dark:text-bodydark2">
              Selecciona una cuenta pendiente disponible para registrar el pago
            </p>
          </div>

          <div
            className="
            inline-flex items-center gap-2
            rounded-md
            border border-stroke
            bg-gray-2
            px-3 py-2
            text-xs font-medium text-body
            dark:border-strokedark
            dark:bg-meta-4
            dark:text-bodydark2
          "
          >
            <span className="h-2 w-2 rounded-full bg-primary" />
            {filteredOptions.length} registros
          </div>
        </div>

        {/* Results container */}
        <div
          className="
          bg-gray-1
          max-h-[500px]
          overflow-y-auto
          rounded-md border
          border-stroke
          p-3
          dark:border-strokedark
          dark:bg-boxdark-2
          bg-gray
        "
        >
          {filteredOptions.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center px-6 text-center">
              <div
                className="
                mb-4 flex h-14 w-14 items-center justify-center
                rounded-full
                bg-primary bg-opacity-10
                text-primary
                "
              >
                <ChartNoAxesColumn className="h-6 w-6" />
              </div>

              <p className="text-sm font-semibold text-black dark:text-white">
                {emptyMessage}
              </p>

              <p className="mt-2 max-w-sm text-xs text-body dark:text-bodydark2">
                Intenta cambiar el cliente seleccionado o ajustar los filtros de
                búsqueda para encontrar cuentas disponibles.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredOptions.map((item) => (
                <ReceivableSelectionItem
                  key={item.id}
                  item={item}
                  isActive={item.id === selectedValue}
                  disabled={disabled}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchSelectionCard;
